import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trash2, AlertTriangle, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

interface RemoveFromCartAlertProps {
  isOpen: boolean;
  itemTitle: string;
  itemType?: string;
  onClose: () => void;
  onConfirm: () => void;
}

const RemoveFromCartAlert = ({ isOpen, itemTitle, itemType, onClose, onConfirm }: RemoveFromCartAlertProps) => {
  const { getDictValue } = useAuth();

  const isRentalOrExperience =
    itemType === "AIRCRAFT_RENTAL" ||
    itemType === "EXPERIENCE";

  // Helper function to map and format the product type label
  const getProductTypeLabel = (type?: string) => {
    if (!type) return "UNIT_ASSET";
    const t = type.toUpperCase();
    if (t.includes("PART")) return "AIRCRAFT_PART";
    if (t.includes("RENTAL")) return "FLIGHT_RENTAL";
    if (t.includes("EXPERIENCE")) return "AVIATION_EXPERIENCE";
    return "MARKETPLACE_ASSET";
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-[6px] p-6">
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="relative w-full max-w-md overflow-hidden rounded-[2.5rem] bg-[#0F172A] border border-red-500/30 shadow-[0_0_50px_rgba(239,68,68,0.15)]"
          >
            {/* Margine superioară decorativă Crimson Red */}
            <div className="h-2 bg-gradient-to-r from-red-600 via-rose-500 to-red-600" />

            <div className="p-10">
              {/* Secțiune HUD / Header */}
              <div className="flex items-center gap-5 mb-8">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-red-500/10 text-red-500 border border-red-500/20 shadow-[0_0_20px_rgba(239,68,68,0.2)]">
                  <Trash2 size={30} />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-black tracking-[0.3em] text-red-500 uppercase font-mono">
                    {getProductTypeLabel(itemType)} // {isRentalOrExperience ? "CANCELLATION" : "DISPOSAL"}
                  </p>
                  <h3 className="text-2xl font-black text-white italic leading-tight truncate">
                    {isRentalOrExperience ? "Anulare Rezervare" : "Eliminare Active"}
                  </h3>
                </div>
              </div>

              {/* Mesaj Conținut adaptat în funcție de tipul produsului */}
              <div className="space-y-5 mb-10">
                <p className="text-slate-300 text-sm leading-relaxed">
                  {isRentalOrExperience ? (
                    <>
                      Sigur doriți să <span className="text-red-400 font-bold">anulați</span> această rezervare? Un e-mail va fi trimis automat operatorului pentru a-l anunța de anularea dumneavoastră, iar sloturile din turnul de control vor fi eliberate.
                    </>
                  ) : (
                    "Sigur doriți să eliminați această unitate din hangarul de rezervări curente? Această acțiune va debloca sloturile alocate în turnul de control."
                  )}
                </p>

                <div className="rounded-2xl bg-red-500/5 border border-red-500/10 p-4">
                  <div className="flex gap-3 items-start text-xs text-slate-400">
                    <div className="mt-0.5 shrink-0">
                      <AlertTriangle size={14} className="text-red-500 animate-pulse" />
                    </div>
                    <div className="space-y-1 min-w-0">
                      <p className="text-[10px] font-mono text-slate-500 uppercase font-bold tracking-wider leading-none">
                        {isRentalOrExperience ? "Rezervare selectată:" : "Unitate selectată:"}
                      </p>
                      <p className="text-white font-bold uppercase tracking-tight font-sans truncate">
                        {itemTitle || "Produs Aviație"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Butoane Tactice Directe */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={onConfirm}
                  className="w-full h-14 px-6 rounded-xl bg-red-600 text-white text-xs font-black uppercase tracking-widest hover:bg-red-500 transition-all shadow-[0_0_20px_rgba(239,68,68,0.2)] flex items-center justify-center gap-3 group active:scale-[0.98]"
                >
                  <Trash2 size={15} className="group-hover:animate-bounce" />
                  {isRentalOrExperience ? "Confirmă Anularea" : "Confirmă Eliminarea"}
                </button>
                <button
                  onClick={onClose}
                  className="w-full py-3 rounded-xl text-slate-500 text-[10px] font-black uppercase tracking-widest hover:text-slate-300 transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowLeft size={12} /> Păstrează în Coș
                </button>
              </div>
            </div>

            {/* Logo watermark decorativ pe fundal */}
            <div className="absolute -bottom-16 -right-16 opacity-[0.02] pointer-events-none text-white">
              <Trash2 size={240} />
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default RemoveFromCartAlert;